import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import FallbackFooter from "../FallbackFooter";
import { fetchUHFFooter, UHFFooterData } from "./uhfService";

type LoadState = "loading" | "loaded" | "error";

/**
 * UHF Footer component with automatic fallback.
 *
 * - On mount: fetches UHF footer HTML for the current locale
 * - On language change: re-fetches for the new locale
 * - On error/timeout: renders FallbackFooter (user never sees an error)
 * - Caches successful responses in sessionStorage
 */
const UHFFooter: React.FC = () => {
  const { i18n } = useTranslation();
  const [state, setState] = useState<LoadState>("loading");
  const [uhfData, setUhfData] = useState<UHFFooterData | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const currentLocale = i18n.language || "zh";

  const loadFooter = useCallback(async (locale: string) => {
    setState("loading");
    const data = await fetchUHFFooter(locale);
    if (data) {
      setUhfData(data);
      setState("loaded");
    } else {
      setState("error");
    }
  }, []);

  // Load on mount and when language changes
  useEffect(() => {
    loadFooter(currentLocale);
  }, [currentLocale, loadFooter]);

  // Inject UHF HTML, CSS, and JS when data is loaded
  useEffect(() => {
    if (state !== "loaded" || !uhfData || !footerRef.current) return;

    // Inject CSS: parse the raw HTML to extract <style> and <link> tags
    if (uhfData.cssHtml) {
      const cssContainer = document.createDocumentFragment();
      const temp = document.createElement("div");
      temp.innerHTML = uhfData.cssHtml;
      Array.from(temp.children).forEach((el) => {
        const tagName = el.tagName.toLowerCase();
        if (tagName === "style") {
          const style = document.createElement("style");
          style.textContent = el.textContent;
          cssContainer.appendChild(style);
        } else if (tagName === "link") {
          const href = el.getAttribute("href");
          if (href && !document.querySelector(`link[href="${href}"]`)) {
            const link = document.createElement("link");
            link.rel = el.getAttribute("rel") || "stylesheet";
            link.href = href;
            cssContainer.appendChild(link);
          }
        }
      });
      document.head.appendChild(cssContainer);
    }

    // Override UHF's hiding of undefined Web Components globally.
    // UHF external CSS has rules like `uhf-footer-nav:not(:defined) { visibility: hidden }`
    // that keep content invisible when Web Components aren't registered by UHF JS.
    if (!document.querySelector("style[data-uhf-override]")) {
      const override = document.createElement("style");
      override.setAttribute("data-uhf-override", "true");
      override.textContent = `
        uhf-footer:not(:defined),
        uhf-footer-nav:not(:defined),
        uhf-brand:not(:defined),
        uhf-contextual-nav:not(:defined),
        uhf-global-nav:not(:defined),
        uhf-mecontrol:not(:defined),
        uhf-dropdown:not(:defined),
        uhf-popout:not(:defined) {
          visibility: visible !important;
          display: block !important;
        }
      `;
      document.head.appendChild(override);
    }

    // Inject footer HTML
    footerRef.current.innerHTML = uhfData.footerHtml;

    // Inject JS: ensure scripts load in order.
    // Use sequential loading for external scripts to maintain proper initialization.
    if (uhfData.jsHtml) {
      const temp = document.createElement("div");
      temp.innerHTML = uhfData.jsHtml;
      const scripts = Array.from(temp.querySelectorAll("script"));

      const loadScript = (el: HTMLScriptElement): Promise<void> => {
        return new Promise((resolve) => {
          const script = document.createElement("script");
          const src = el.getAttribute("src");
          const type = el.getAttribute("type");
          if (type) script.type = type;

          if (src) {
            if (document.querySelector(`script[src="${src}"]`)) {
              resolve();
              return;
            }
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => resolve();
          } else if (el.textContent) {
            script.textContent = el.textContent;
          }

          document.body.appendChild(script);
          if (!src) resolve();
        });
      };

      // Load scripts sequentially to maintain dependency order
      (async () => {
        for (const el of scripts) {
          await loadScript(el as HTMLScriptElement);
        }
      })();
    }
  }, [state, uhfData]);

  // During loading or on error, render the FallbackFooter
  // FallbackFooter already includes ICP links, so no ICPInfo needed here
  if (state === "loading" || state === "error") {
    return <FallbackFooter />;
  }

  // UHF loaded successfully
  return (
    <div className="uhf-footer-wrapper" style={{ flexShrink: 0, width: "100%", marginTop: "-45px" }} ref={footerRef} />
  );
};

export default UHFFooter;
